import React, { useCallback, useMemo, useState } from 'react';
import {
  QBodyLayout,
  QBox,
  QStack,
  QTab,
  QTabList,
  QTabPanel,
  QTabPanels,
  QTabs,
  useToastProvider,
} from '@qualio/ui-components';
import { TemplateDetail, TemplateVersions, UpdateSection, UpdateSectionSchema } from '../../types';
import { TemplateDetailsHeader } from './TemplateDetailsHeader';
import { TemplateDetailsProperty } from './TemplateDetailsProperty';
import { TemplateDetailsBody } from './TemplateDetailsBody';
import { NoSectionsPanel } from '../NoSectionsPanel/NoSectionsPanel';
import { ChangeControlSettingsTab } from './ChangeControlSettingsTab';
import { useSearchParams } from 'react-router-dom';
import { InlineEditSectionsForm } from '../EditSectionsForm/InlineEditSectionsForm';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useUpdateTemplateDraft } from '../../hooks/useUpdateTemplateDraft';
import { extractMessageFromError } from '../../utils/backEndErrorMessages';
import { useTranslation } from 'react-i18next';
import { useAllCCWorkflows } from '../../hooks/useAllCCWorkflows';
import { useLinkedCCWorkflows } from '../../hooks/useLinkedCCWorkflows';
import { useUpdateTemplateLinks } from '../../hooks/useUpdateTemplateLinks';
import { CCWorkflowLink, getActiveMatrixIds, isArchivedWorkflow } from '../../types/ccWorkflow';
import { useTemplateActions } from '../../hooks/useTemplateActions';

export const TemplateDetails: React.FC<{ data: TemplateDetail; versions: TemplateVersions }> = ({ data, versions }) => {
  const { sections, company_id, id } = data;
  const sectionExists = sections && sections.length > 0;
  const { isInherited, canViewChangeControl } = useTemplateActions(data);
  const showChangeControlTabs = canViewChangeControl;
  const [searchParams, setSearchParams] = useSearchParams();
  const isEditing = searchParams.get('intent') === 'edit' && !isInherited;
  const { t } = useTranslation();
  const { showToast } = useToastProvider();
  const [selectionOverrides, setSelectionOverrides] = useState<Record<number, boolean>>({});
  const { mutateAsync: updateTemplateDraftMutate } = useUpdateTemplateDraft(data.template_uuid);
  const { mutateAsync: updateTemplateLinks } = useUpdateTemplateLinks(id);
  const { data: allWorkflowsMaybe = [], isLoading } = useAllCCWorkflows(company_id, id, showChangeControlTabs);
  const { data: linkedWorkflowsMaybe = [], isLoading: isLinkedLoading } = useLinkedCCWorkflows(
    company_id,
    id,
    showChangeControlTabs,
  );

  const wfsForSavePayload = useMemo(() => {
    return allWorkflowsMaybe.filter((wf: CCWorkflowLink) => {
      // return early on user selected items, else return items already linked
      if (wf.id in selectionOverrides) return selectionOverrides[wf.id];
      // qualio managed templates are selected by default
      return wf.is_linked || wf.qualio_managed;
    });
  }, [allWorkflowsMaybe, selectionOverrides]);

  const displaySelectedWfs = useMemo(
    () => wfsForSavePayload.filter((wf) => !isArchivedWorkflow(wf)),
    [wfsForSavePayload],
  );

  const hasLinkedArchivedWfs = useMemo(
    () => linkedWorkflowsMaybe.some((wf) => isArchivedWorkflow(wf)),
    [linkedWorkflowsMaybe],
  );

  const editableWorkflows = useMemo(
    () => allWorkflowsMaybe.filter((wf) => !isArchivedWorkflow(wf) || wf.is_linked),
    [allWorkflowsMaybe],
  );

  const formMethods = useForm<UpdateSection>({
    mode: 'onChange',
    resolver: zodResolver(UpdateSectionSchema),
    defaultValues: { sections: sections ?? [] },
  });

  const handleSaveSections = useCallback(async (): Promise<boolean> => {
    let saved = false;
    await formMethods.handleSubmit(
      async (updateData: UpdateSection) => {
        const payload = {
          ...data,
          prefix: data.prefix.replace('-', ''),
          sections: updateData.sections.map((section, index) => ({
            id: section.id || null,
            title: section.title,
            position: index + 1,
            content: section.content,
            type: section.type || 'text',
          })),
          resolutions: [],
          file_download_user_groups: data.file_download_user_groups || [],
          tag_ids: data.tag_ids ?? [],
        };
        await updateTemplateDraftMutate(payload, {
          onSuccess: () => {
            saved = true;
            setSearchParams({});
          },
          onError: (error: any) => {
            showToast({
              description: extractMessageFromError(error),
              status: 'error',
              title: t('template.sections.errors.failedToUpdate'),
              'data-cy': 'toast-template-details-save-error',
            });
          },
        });
      },
      () =>
        showToast({
          description: t('template.sections.errors.invalidSections'),
          status: 'error',
          title: t('template.sections.errors.failedToUpdate'),
          'data-cy': 'toast-template-details-invalid-sections-error',
        }),
    )();
    return saved;
  }, [data, formMethods, setSearchParams, showToast, t, updateTemplateDraftMutate]);

  const handleSaveCCLinks = useCallback(async () => {
    const hasOverrides = Object.keys(selectionOverrides).length > 0;
    const hasNewLinkedWfs = wfsForSavePayload.length > linkedWorkflowsMaybe.length;
    if (hasOverrides || hasNewLinkedWfs || hasLinkedArchivedWfs) {
      await updateTemplateLinks(getActiveMatrixIds(wfsForSavePayload), { onSuccess: () => setSearchParams({}) });
    }
  }, [
    selectionOverrides,
    wfsForSavePayload,
    linkedWorkflowsMaybe,
    hasLinkedArchivedWfs,
    updateTemplateLinks,
    setSearchParams,
  ]);

  const handleStripArchivedCCLinks = useCallback(async () => {
    if (hasLinkedArchivedWfs) {
      await updateTemplateLinks(getActiveMatrixIds(linkedWorkflowsMaybe));
    }
  }, [hasLinkedArchivedWfs, linkedWorkflowsMaybe, updateTemplateLinks]);

  const handleSaveChanges = useCallback(async () => {
    const sectionsSaved = await handleSaveSections();
    if (sectionsSaved) {
      await handleSaveCCLinks();
    }
  }, [handleSaveSections, handleSaveCCLinks]);

  const handleChangeSelection = (checked: boolean, ccLink: CCWorkflowLink) => {
    setSelectionOverrides((prev) => ({ ...prev, [ccLink.id]: checked }));
  };

  // first time users won't have the system template linked, so we need to add here so that the UI shows it as linked
  const linkedWfsWithSystemTemplate =
    linkedWorkflowsMaybe.length > 0 ? linkedWorkflowsMaybe : allWorkflowsMaybe.filter((wf) => wf.qualio_managed);

  const sectionsContent = isEditing ? (
    <InlineEditSectionsForm data={data} />
  ) : (
    <>{sectionExists ? <TemplateDetailsBody data={data} /> : <NoSectionsPanel template={data} />}</>
  );

  return (
    <FormProvider {...formMethods}>
      <QBodyLayout.Default minWidth={'829px'}>
        <TemplateDetailsHeader
          data={data}
          versions={versions}
          onSaveSections={handleSaveChanges}
          onBeforeMakeEffective={handleStripArchivedCCLinks}
        />
        {showChangeControlTabs ? (
          <QTabs defaultIndex={0} variant="enclosed">
            <QTabList>
              <QTab data-cy="sections-tab">Sections</QTab>
              <QTab data-cy="cc-settings-tab">Change control settings</QTab>
            </QTabList>
            <QStack direction="row">
              <QTabPanels width="100%">
                <QTabPanel>
                  <QBox mt={4}>{sectionsContent}</QBox>
                </QTabPanel>
                <QTabPanel>
                  <ChangeControlSettingsTab
                    isLoading={isLoading || isLinkedLoading}
                    workflows={isEditing ? editableWorkflows : linkedWfsWithSystemTemplate}
                    selectedWorkflows={isEditing ? displaySelectedWfs : linkedWfsWithSystemTemplate}
                    onChangeSelection={handleChangeSelection}
                  />
                </QTabPanel>
              </QTabPanels>
              <TemplateDetailsProperty data={data} />
            </QStack>
          </QTabs>
        ) : (
          <QStack direction="row">
            {sectionsContent}
            <TemplateDetailsProperty data={data} />
          </QStack>
        )}
      </QBodyLayout.Default>
    </FormProvider>
  );
};
